/**
 * Écran « Masse salariale ».
 *
 * Les charges de personnel pèsent 71 % du budget : elles se pilotent salarié
 * par salarié. La grille de bruts individuels court de 2008 à 2030, ce qui
 * permet de projeter la masse sans postuler de taux d'évolution global.
 */

import {
  PLANCHER_TRESORERIE,
  dateFr,
  entier,
  euros,
  eurosCentimes,
  eurosSigne,
  nombre,
  nomAffiche,
  pourcent,
} from '@scc/core';
import type { Charge } from '../api.js';
import { h } from '../dom.js';
import { TEINTES, graphiqueLignes, legende } from '../graphiques.js';
import { badge, enteteSection, insight, noteLecture } from '../composants.js';

export function vueMasseSalariale(
  d: Charge,
  changerTrajectoire: (cle: string) => void = () => undefined,
): HTMLElement {
  const p = d.paie;
  if (!p) {
    return h(
      'div',
      { classe: 'page', id: 'page-masse-salariale', role: 'region', 'aria-label': 'Masse salariale' },
      h('div', { classe: 'entete-page' }, h('div', {}, h('h1', {}, 'Masse salariale'))),
      h(
        'div',
        { classe: 'erreur-bloc' },
        'Aucune donnée de paie ingérée. Lancez une ingestion depuis l’écran d’exploitation.',
      ),
    );
  }

  const courant = p.exerciceCourant;
  const an = d.meta.exerciceCourant;
  const budgete = d.previsionnelVote.charges.personnel;
  const ecart = courant.masseChargee - budgete;

  /** Taux de croissance annuel moyen de la masse sur la période observée. */
  const premier = p.serie[0];
  const dernier = p.serie[p.serie.length - 1];
  const croissanceAnnuelle =
    premier && dernier && p.serie.length > 1 && premier.masseChargee > 0
      ? (dernier.masseChargee / premier.masseChargee) ** (1 / (p.serie.length - 1)) - 1
      : 0;

  // ── Trajectoire de la masse ───────────────────────────────────────────
  const annees = p.serie.map((m) => m.exercice);
  const graphMasse = h(
    'div',
    {},
    graphiqueLignes(
      annees,
      [
        {
          nom: 'Salaires bruts',
          couleur: TEINTES.charcoal,
          epaisseur: 3,
          valeurs: p.serie.map((m) => m.brut),
        },
        {
          nom: 'Charges patronales',
          couleur: TEINTES.amber,
          valeurs: p.serie.map((m) => m.chargesPatronales),
        },
        {
          nom: 'Masse chargée',
          couleur: TEINTES.rose,
          epaisseur: 3,
          valeurs: p.serie.map((m) => m.masseChargee),
        },
      ],
      { titre: 'Trajectoire de la masse salariale', enteteAbscisse: 'Exercice', depuisZero: true },
    ),
    legende([
      { nom: 'Salaires bruts', couleur: TEINTES.charcoal },
      { nom: 'Charges patronales', couleur: TEINTES.amber },
      { nom: 'Masse chargée', couleur: TEINTES.rose },
    ]),
  );

  // ── Trajectoire individuelle ──────────────────────────────────────────
  const couleurs = [TEINTES.charcoal, TEINTES.green, TEINTES.blue, TEINTES.amber, TEINTES.slate];
  const graphIndividuel = h(
    'div',
    {},
    graphiqueLignes(
      annees,
      p.salaries.map((s, i) => ({
        nom: nomAffiche(s),
        couleur: couleurs[i % couleurs.length] ?? TEINTES.slate,
        epaisseur: 2.5,
        valeurs: annees.map((a) => {
          const m = p.serie.find((x) => x.exercice === a);
          return m?.situations.find((x) => x.salarie.id === s.id)?.brutAnnuel ?? null;
        }),
      })),
      { unite: 'euros', titre: 'Brut annuel par salarié', enteteAbscisse: 'Exercice', depuisZero: true },
    ),
    legende(
      p.salaries.map((s, i) => ({
        nom: nomAffiche(s),
        couleur: couleurs[i % couleurs.length] ?? TEINTES.slate,
      })),
    ),
  );

  // ── Fiches individuelles ──────────────────────────────────────────────
  const fiches = h(
    'div',
    { classe: 'fiches' },
    ...courant.situations.map((s) =>
      h(
        'article',
        { classe: 'fiche' },
        h('p', { classe: 'nom' }, nomAffiche(s.salarie)),
        h('p', { classe: 'fonction' }, s.salarie.titre ?? '—'),
        h(
          'dl',
          {},
          h('dt', {}, 'Brut mensuel en fin d’exercice'),
          h('dd', {}, eurosCentimes(s.brutMensuelFin)),
          h('dt', {}, 'Brut annuel'),
          h('dd', {}, eurosCentimes(s.brutAnnuel)),
          h('dt', {}, 'Charges patronales'),
          h('dd', {}, eurosCentimes(s.chargesPatronales)),
          h('dt', {}, 'Coût employeur'),
          h('dd', {}, eurosCentimes(s.coutEmployeur)),
          h('dt', {}, 'Groupe et échelon'),
          h('dd', {}, s.groupe !== null ? `G${s.groupe} · É${s.echelon ?? '—'}` : '—'),
          h('dt', {}, 'Ancienneté'),
          h('dd', {}, s.salarie.dateEntree ? `depuis le ${dateFr(s.salarie.dateEntree)}` : '—'),
          h('dt', {}, 'Progression sur un an'),
          h(
            'dd',
            {},
            s.progression === null
              ? '—'
              : badge(
                  s.progression >= 0 ? 'favorable' : 'alerte',
                  `${s.progression >= 0 ? '▲' : '▼'} ${pourcent(s.progression, 1)}`,
                ),
          ),
          h('dt', {}, 'Mois renseignés'),
          h('dd', {}, `${entier(s.moisRenseignes)} / 12`),
        ),
      ),
    ),
  );

  // ── Tableau pluriannuel ───────────────────────────────────────────────
  const tableSerie = h(
    'div',
    { classe: 'enveloppe-table' },
    h(
      'table',
      { classe: 'table-budget' },
      h('caption', { classe: 'invisible' }, 'Masse salariale par exercice'),
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { scope: 'col' }, 'Exercice'),
          h('th', { scope: 'col' }, 'Effectif'),
          h('th', { scope: 'col' }, 'Salaires bruts'),
          h('th', { scope: 'col' }, 'Charges patronales'),
          h('th', { scope: 'col' }, 'Masse chargée'),
          h('th', { scope: 'col' }, 'Évolution'),
        ),
      ),
      h(
        'tbody',
        {},
        ...p.serie.map((m, i) => {
          const prec = p.serie[i - 1];
          const evol = prec && prec.masseChargee > 0 ? m.masseChargee / prec.masseChargee - 1 : null;
          return h(
            'tr',
            { classe: m.exercice === an ? 'categorie' : '' },
            h('th', { scope: 'row' }, `${m.exercice}${m.exerciceIncomplet ? ' *' : ''}`),
            h('td', {}, entier(m.effectif)),
            h('td', {}, euros(m.brut)),
            h('td', {}, euros(m.chargesPatronales)),
            h('td', {}, euros(m.masseChargee)),
            h('td', {}, evol === null ? '—' : pourcent(evol, 1)),
          );
        }),
      ),
    ),
  );

  // ── Rapprochement avec les écritures ──────────────────────────────────
  const tableVentilation = h(
    'table',
    { classe: 'comp-table' },
    h('caption', { classe: 'invisible' }, `Écritures de paie ${an} par nature`),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Nature'),
        h('th', { scope: 'col' }, 'Charge (classe 6)'),
        h('th', { scope: 'col' }, 'Dette (classe 4)'),
        h('th', { scope: 'col' }, 'Écritures'),
      ),
    ),
    h(
      'tbody',
      {},
      ...p.ventilation.map((v) =>
        h(
          'tr',
          {},
          h('th', { scope: 'row' }, v.nature),
          h('td', { classe: 'num' }, eurosCentimes(v.charge)),
          h('td', { classe: 'num' }, v.tiers === 0 ? '—' : eurosCentimes(v.tiers)),
          h('td', {}, entier(v.nbEcritures)),
        ),
      ),
    ),
  );

  // ── Trajectoires salariales ───────────────────────────────────────────
  const choixTrajectoire = h(
    'div',
    { classe: 'tabs', role: 'tablist', 'aria-label': 'Trajectoires salariales' },
    ...p.trajectoiresDisponibles.map((t) =>
      h(
        'button',
        {
          classe: 'tab',
          type: 'button',
          role: 'tab',
          title: t.description,
          'aria-selected': t.cle === p.trajectoire ? 'true' : 'false',
          surClick: () => {
            changerTrajectoire(t.cle);
          },
        },
        t.libelle,
      ),
    ),
  );

  const trajectoireActive = p.trajectoiresDisponibles.find((t) => t.cle === p.trajectoire);

  // ── Primes de partage de la valeur ────────────────────────────────────
  const primesExercice = p.primes.filter((x) => x.moisVersement.startsWith(String(an)));
  const totalPrimes = primesExercice.reduce((s, x) => s + x.montantBrut, 0);

  const tablePrimes = h(
    'table',
    { classe: 'comp-table' },
    h('caption', { classe: 'invisible' }, `Primes versées en ${an}`),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Salarié'),
        h('th', { scope: 'col' }, 'Mois'),
        h('th', { scope: 'col' }, 'Type'),
        h('th', { scope: 'col' }, 'Montant brut'),
        h('th', { scope: 'col' }, 'Régime social'),
        h('th', { scope: 'col' }, 'Motif'),
      ),
    ),
    h(
      'tbody',
      {},
      ...(primesExercice.length === 0
        ? [
            h(
              'tr',
              {},
              h('th', { scope: 'row' }, 'Aucune prime enregistrée'),
              h('td', { colspan: '5' }, `sur l’exercice ${an}`),
            ),
          ]
        : primesExercice.map((x) => {
            const s = p.salaries.find((y) => y.id === x.salarie);
            return h(
              'tr',
              {},
              h('th', { scope: 'row' }, s ? nomAffiche(s) : '—'),
              h('td', {}, x.moisVersement),
              h('td', {}, x.type),
              h('td', { classe: 'num' }, eurosCentimes(x.montantBrut)),
              h(
                'td',
                {},
                badge(
                  x.exonerationPresumee ? 'favorable' : 'vigilance',
                  x.exonerationPresumee ? 'Exonérée' : 'Chargée',
                ),
              ),
              h('td', {}, x.motif ?? '—'),
            );
          })),
    ),
  );

  // ── Coût de rupture ───────────────────────────────────────────────────
  const anneesRupture = p.rupture.map((r) => r.exercice);
  const tableRupture = h(
    'div',
    { classe: 'enveloppe-table' },
    h(
      'table',
      { classe: 'table-budget' },
      h('caption', { classe: 'invisible' }, 'Coût de rupture par salarié et par exercice'),
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { scope: 'col' }, 'Salarié'),
          ...anneesRupture.map((a) => h('th', { scope: 'col' }, `Fin ${a}`)),
        ),
      ),
      h(
        'tbody',
        {},
        ...p.salaries.map((s) =>
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, nomAffiche(s)),
            ...p.rupture.map((r) => {
              const c = r.parSalarie.find((x) => x.salarie.id === s.id);
              return h('td', {}, c ? euros(c.coutTotal) : '—');
            }),
          ),
        ),
        h(
          'tr',
          { classe: 'total' },
          h('th', { scope: 'row' }, 'Coût cumulé de l’effectif entier'),
          ...p.rupture.map((r) => h('td', {}, euros(r.coutTotalEffectif))),
        ),
      ),
    ),
  );

  const ruptureCourante = p.rupture.find((r) => r.exercice === an);
  const detailRupture = ruptureCourante
    ? h(
        'table',
        { classe: 'comp-table' },
        h('caption', { classe: 'invisible' }, `Décomposition du coût de rupture au 31 décembre ${an}`),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'col' }, 'Salarié'),
            h('th', { scope: 'col' }, 'Ancienneté'),
            h('th', { scope: 'col' }, 'Salaire de référence'),
            h('th', { scope: 'col' }, 'Indemnité'),
            h('th', { scope: 'col' }, 'Préavis'),
            h('th', { scope: 'col' }, 'Congés payés'),
            h('th', { scope: 'col' }, 'Charges'),
            h('th', { scope: 'col' }, 'Total'),
          ),
        ),
        h(
          'tbody',
          {},
          ...ruptureCourante.parSalarie.map((c) =>
            h(
              'tr',
              {},
              h('th', { scope: 'row' }, nomAffiche(c.salarie)),
              h('td', {}, `${nombre(c.anciennete, 1)} ans`),
              h('td', { classe: 'num' }, eurosCentimes(c.salaireReference)),
              h('td', { classe: 'num' }, `${eurosCentimes(c.indemniteLicenciement)} (${nombre(c.moisIndemnite, 2)} mois)`),
              h('td', { classe: 'num' }, `${eurosCentimes(c.preavis)} (${entier(c.moisPreavis)} mois)`),
              h('td', { classe: 'num' }, eurosCentimes(c.congesPayes)),
              h('td', { classe: 'num' }, eurosCentimes(c.chargesPatronales)),
              h('td', { classe: 'num' }, h('strong', {}, eurosCentimes(c.coutTotal))),
            ),
          ),
        ),
      )
    : null;

  // Le seuil de sécurité provisionne implicitement la rupture du contrat de
  // direction : le rapprochement dit si la provision suffit.
  const provisionSeuil = d.chainage.seuilSecurite - PLANCHER_TRESORERIE;
  const coutDirection = ruptureCourante?.coutMaximal;
  const provisionSuffisante = coutDirection ? provisionSeuil >= coutDirection.coutTotal : true;

  return h(
    'div',
    { classe: 'page', id: 'page-masse-salariale', role: 'region', 'aria-label': 'Masse salariale' },
    h(
      'div',
      { classe: 'entete-page' },
      h(
        'div',
        {},
        h('h1', {}, 'Masse salariale'),
        h(
          'p',
          { classe: 'chapeau' },
          `${entier(courant.effectif)} salarié${courant.effectif > 1 ? 's' : ''}, ` +
            `${euros(courant.masseChargee)} de masse chargée projetée pour ${an}, ` +
            `soit ${pourcent(d.projection.chargesProjetees > 0 ? courant.masseChargee / d.projection.chargesProjetees : 0, 0)} ` +
            `des charges de l’exercice.`,
        ),
      ),
      h(
        'div',
        { classe: 'reperes' },
        h('span', {}, `Taux de charges ${pourcent(courant.taux, 1)}`),
        h('span', {}, courant.tauxConstate ? 'taux constaté' : 'taux estimé'),
      ),
    ),

    h(
      'section',
      {},
      enteteSection(1, `Vue d’ensemble ${an}`, 'Masse chargée confrontée à l’enveloppe budgétée'),
      h(
        'div',
        { classe: 'grille-bord' },
        h(
          'div',
          { classe: 'tuile' },
          h('h3', {}, 'Masse chargée projetée'),
          h('p', { classe: 'chiffre-cle' }, euros(courant.masseChargee)),
          h(
            'p',
            { classe: 'sous-chiffre' },
            `contre ${euros(budgete)} budgétés, soit un écart de `,
            h(
              'span',
              { classe: ecart <= 0 ? 'ecart-positif' : 'ecart-negatif' },
              `${ecart <= 0 ? '−' : '+'}${euros(Math.abs(ecart))}`,
            ),
          ),
        ),
        h(
          'div',
          { classe: 'tuile' },
          h('h3', {}, 'Décomposition'),
          h(
            'dl',
            { classe: 'fiche' },
            h('dt', {}, 'Salaires bruts'),
            h('dd', {}, euros(courant.brut)),
            h('dt', {}, 'Charges patronales'),
            h('dd', {}, euros(courant.chargesPatronales)),
            h('dt', {}, 'Titres-restaurant'),
            h('dd', {}, euros(courant.titresRestaurant)),
          ),
        ),
        h(
          'div',
          { classe: 'tuile' },
          h('h3', {}, 'Taux de charges patronales'),
          h('p', { classe: 'chiffre-cle' }, pourcent(courant.taux, 2)),
          h(
            'p',
            { classe: 'sous-chiffre' },
            courant.tauxConstate
              ? 'Rapport des cotisations patronales au brut, constaté sur les écritures de paie de classe 6.'
              : 'Aucune écriture exploitable : taux de repli déclaré en configuration.',
          ),
        ),
      ),
      noteLecture(
        'Les comptes de classe 4 qui accompagnent une écriture de paie — URSSAF, autres organismes, ' +
          'personnel — enregistrent une dette, pas une charge. Ils sont exclus du taux.',
      ),
    ),

    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(2, 'Salaires individuels', `Situation de chaque salarié sur l’exercice ${an}`),
      fiches,
      insight(
        'info',
        'ℹ',
        `**La grille court jusqu’en ${annees[annees.length - 1] ?? ''}.** Les bruts individuels sont ` +
          `tenus mois par mois dans la base de paie, projections comprises. La masse prévisionnelle ` +
          `n’est donc pas estimée par un taux global : elle se calcule salarié par salarié.`,
      ),
    ),

    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(3, 'Trajectoire individuelle', 'Brut annuel de chaque salarié'),
      graphIndividuel,
    ),

    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(
        4,
        `Trajectoires salariales ${an}-${annees[annees.length - 1] ?? ''}`,
        'Comparer plusieurs hypothèses d’évolution des rémunérations',
      ),
      h(
        'p',
        { classe: 'text-block' },
        `La grille tenue dans la base porte déjà une projection jusqu’en ${annees[annees.length - 1] ?? ''}, ` +
          `échelons et anciennetés compris : c’est la trajectoire de référence. Les autres se calculent depuis ` +
          `le dernier exercice réel, en appliquant une revalorisation générale annuelle. Le choix se répercute ` +
          `immédiatement sur le budget pluriannuel et sur les trajectoires de réserve.`,
      ),
      choixTrajectoire,
      trajectoireActive
        ? noteLecture(`Trajectoire retenue — ${trajectoireActive.libelle} : ${trajectoireActive.description}`)
        : null,
      graphMasse,
      tableSerie,
      noteLecture(
        'Un astérisque signale un exercice dont un salarié au moins compte moins de douze mois ' +
          'renseignés : entrée, sortie, ou grille incomplète.',
      ),
    ),

    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(
        5,
        'Primes de partage de la valeur',
        'Dites « primes Macron » — exonérées de cotisations dans la limite du plafond annuel',
      ),
      h(
        'p',
        { classe: 'text-block' },
        `${eurosCentimes(totalPrimes)} de primes enregistrées sur l’exercice ${an}. La prime de partage de la ` +
          `valeur est exonérée de cotisations sociales dans la limite annuelle par bénéficiaire ; au-delà, la ` +
          `fraction excédentaire est traitée comme du salaire et supporte les charges. Le forfait social ne ` +
          `s’applique qu’aux entreprises d’au moins 250 salariés : le syndicat n’y est pas soumis.`,
      ),
      tablePrimes,
      noteLecture(
        'Le plafond s’apprécie sur le cumul de l’exercice et par bénéficiaire, non prime par prime. ' +
          'Les primes entrent dans la masse chargée et dans le salaire de référence servant au calcul ' +
          'des indemnités de rupture.',
      ),
    ),

    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(
        6,
        'Coût de rupture du contrat de travail',
        'Ce que coûterait un licenciement, salarié par salarié, à la fin de chaque exercice',
      ),
      h(
        'p',
        { classe: 'text-block' },
        `Trois composantes de natures différentes : l’indemnité de licenciement, fonction de l’ancienneté et ` +
          `exonérée de cotisations dans la limite de deux plafonds annuels ; le préavis, travaillé ou indemnisé, ` +
          `qui supporte les charges dans les deux cas ; les congés payés restant dus. Les coefficients sont ceux ` +
          `du barème légal — un quart de mois par année d’ancienneté jusqu’à dix ans, un tiers au-delà — et sont ` +
          `déclarés en configuration : une convention plus favorable se substitue sans toucher au code.`,
      ),
      tableRupture,
      detailRupture,
      noteLecture(
        `Le salaire de référence est la moyenne des mois renseignés de l’exercice, primes comprises. ` +
          `L’ancienneté est comptée au 31 décembre de l’exercice.`,
      ),
      coutDirection
        ? insight(
            provisionSuffisante ? 'positive' : 'warning',
            provisionSuffisante ? '✦' : '⚠',
            `**Le seuil de sécurité provisionne ${euros(provisionSeuil)} pour la rupture du contrat de direction ` +
              `en ${an}.** Le calcul détaillé donne ${eurosCentimes(coutDirection.coutTotal)} pour ` +
              `${nomAffiche(coutDirection.salarie)}, soit ${eurosSigne(provisionSeuil - coutDirection.coutTotal)}. ` +
              (provisionSuffisante
                ? `La provision couvre le coût.`
                : `La provision est insuffisante. L’écart tient probablement à la durée de préavis retenue — le ` +
                  `modèle applique ${entier(coutDirection.moisPreavis)} mois pour un cadre — et à la provision ` +
                  `pour congés payés, que l’estimation votée ne semble pas inclure. Point à instruire avec le ` +
                  `cabinet comptable avant le prochain vote du seuil.`),
          )
        : null,
    ),

    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(7, 'Rapprochement comptable', `Écritures de paie de l’exercice ${an}`),
      tableVentilation,
      noteLecture(
        `Le brut de la grille porte l’engagement de l’exercice entier ; les écritures ne couvrent que ` +
          `les mois déjà traités en paie. L’écart entre les deux est normal en cours d’exercice.`,
      ),
      h(
        'div',
        { classe: 'barre-actions' },
        h('span', { classe: 'titre' }, 'Effectif'),
        ...p.salaries.map((s) =>
          h(
            'span',
            { classe: 'sous-chiffre' },
            `${nomAffiche(s)} — ${s.titre ?? '—'}${s.actif ? '' : ' (sorti)'}`,
          ),
        ),
      ),
      noteLecture(
        `Coût employeur total ${an} : ${eurosCentimes(courant.masseChargee)} pour ` +
          `${entier(courant.effectif)} salarié${courant.effectif > 1 ? 's' : ''}, soit ` +
          `${eurosCentimes(courant.effectif > 0 ? courant.masseChargee / courant.effectif : 0)} en moyenne. ` +
          `Progression annuelle moyenne de la masse sur la période : ${pourcent(croissanceAnnuelle, 1)}.`,
      ),
      noteLecture(`Pente du produit moyen adhérent, pour mémoire : ${nombre(d.adherents.regression.pente, 2)} € par an.`),
    ),
  );
}
